import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { ProviderType } from 'library-explorer';
import { HttpService, FileSystemService } from 'library-explorer';
import { LoadingToastService } from '../loading-toast.service';
import { Capacitor } from '@capacitor/core';
import { Directory, Encoding } from '@capacitor/filesystem';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root'
})
export class DownloadService {

  constructor(
    private readonly loadingToastrService: LoadingToastService,
    private readonly toastr: ToastrService,
    private fileSystemService: FileSystemService,
    private httpService: HttpService) { }

  public downloadPDF(url: string): Observable<any> {
    return this.httpService.getBlobFile(url);
  }

  public downloadFile(url: string, provider: ProviderType, fileName: string, toastStart: boolean = true, fetchFile = true): Observable<void> {
    const decodedUrl = decodeURIComponent(url);

    if (Capacitor.isNativePlatform()) {
      this.downloadAndSaveFile(decodedUrl, fileName);
      return of();
    }

    if (provider === ProviderType.AWS && fetchFile) {
      if (toastStart) {
        this.loadingToastrService.start();
      }

      return this.loadFileAsArrayBuffer(decodedUrl, fileName)
        .pipe(
          finalize(() => this.loadingToastrService.stop())
        )
    }

    window.open(url, '_blank');
    return of();
  }

  private loadFileAsArrayBuffer(url: string, fileName: string): Observable<void> {
    return this.getFileAsArrayBuffer(url)
      .pipe(
        tap((data) => {
          const blob = new Blob([data]);
          this.download(blob, fileName);
        })
      );
  }

  public download(blobData: Blob, fileName: string): void {
    if (Capacitor.isNativePlatform()) {
      // Save the file natively on the device
      this.saveBlobToDevice(blobData, fileName);
      return;
    }

    // Create a download link for the browser
    const url = window.URL.createObjectURL(blobData);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    link.setAttribute('target', '_blank');
    link.click();
  }

  public getFileAsArrayBuffer(fileSrc: string): Observable<any> {
    return this.httpService.getFileAsArrayBuffer(fileSrc);
  }

  private async downloadAndSaveFile(url: string, fileName: string): Promise<void> {
    this.loadingToastrService.start();

    try {
      const data = await this.fileSystemService.downloadBlob(url, (progress: number) => {
        if (progress >= 100) {
          this.loadingToastrService.stop();
        }
      });

      const blobData = new Blob([data]);
      await this.saveBlobToDevice(blobData, fileName);
    } finally {
      this.loadingToastrService.stop();
    }
  }

  private async saveBlobToDevice(blob: Blob, fileName: string): Promise<void> {
    const fileUri = await this.fileSystemService.writeBlobFile(fileName, blob, true, Directory.Documents, Encoding.UTF8, true);
    if (!fileUri) {
      this.toastr.error(`Failed to save file: ${fileName}`);
      return;
    }

    this.toastr.success(`Download completed: ${fileName}`);
  }
}
